import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { 
  MapPin, 
  ArrowRight, 
  Loader2,
  AlertCircle,
  Home,
  User
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface ShippingAddressCollectionProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string;
}

interface AddressData {
  name: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface ApiAddressData {
  name: string | null;
  phone: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

interface Giftee {
  id: string;
  name: string;
  contact: string;
  hasAddress: boolean;
  address: ApiAddressData;
  enteredBy: string | null;
  enteredByName: string | null;
}

interface ShippingInfo {
  giftGroupId: string;
  giftGroupTitle: string;
  typeId: number;
  typeName: string;
  collectingForSelf: boolean;
  membership: {
    id: string;
    role: string;
    workflowState: string;
    hasAddress: boolean;
    address: ApiAddressData;
    enteredBy: string | null;
    enteredByName: string | null;
  };
  giftees: Giftee[];
}

export function ShippingAddressCollection({ onNavigate, giftGroupId }: ShippingAddressCollectionProps) {
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [address, setAddress] = useState<AddressData>({
    name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'USA',
  });
  const [currentGifteeIndex, setCurrentGifteeIndex] = useState(0);

  useEffect(() => {
    loadShippingInfo();
  }, [giftGroupId]);

  const loadShippingInfo = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await api.getShippingAddressInfo(giftGroupId);
      setShippingInfo(data);

      // Pre-fill address if collecting for self and has existing data
      if (data.collectingForSelf && data.membership.hasAddress) {
        setAddress({
          name: data.membership.address.name || '',
          phone: data.membership.address.phone || '',
          street: data.membership.address.street || '',
          city: data.membership.address.city || '',
          state: data.membership.address.state || '',
          postalCode: data.membership.address.postalCode || '',
          country: data.membership.address.country || 'USA',
        });
      } else if (!data.collectingForSelf && data.giftees.length > 0) {
        // Pre-fill with first giftee's existing address if available
        const firstGiftee = data.giftees[0];
        if (firstGiftee.hasAddress) {
          setAddress({
            name: firstGiftee.address.name || firstGiftee.name || '',
            phone: firstGiftee.address.phone || '',
            street: firstGiftee.address.street || '',
            city: firstGiftee.address.city || '',
            state: firstGiftee.address.state || '',
            postalCode: firstGiftee.address.postalCode || '',
            country: firstGiftee.address.country || 'USA',
          });
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load shipping info';
      setError(errorMessage);
      toast.error('Failed to load shipping information', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!shippingInfo) return;

    try {
      setIsSaving(true);

      const targetMemberId = shippingInfo.collectingForSelf 
        ? undefined 
        : shippingInfo.giftees[currentGifteeIndex]?.id;

      await api.updateShippingAddress(giftGroupId, {
        targetMemberId,
        skip: true,
      });

      // If collecting for multiple giftees, move to next
      if (!shippingInfo.collectingForSelf && currentGifteeIndex < shippingInfo.giftees.length - 1) {
        setCurrentGifteeIndex(currentGifteeIndex + 1);
        setAddress({
          name: '',
          phone: '',
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'USA',
        });
        toast.success('Skipped - moving to next giftee');
        setIsSaving(false);
        return;
      }

      toast.success('Shipping address collection completed');
      onNavigate('dashboard', { tab: 'todo' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to skip';
      toast.error('Failed to skip', {
        description: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!shippingInfo) return;

    try {
      setIsSaving(true);

      const targetMemberId = shippingInfo.collectingForSelf 
        ? undefined 
        : shippingInfo.giftees[currentGifteeIndex]?.id;

      await api.updateShippingAddress(giftGroupId, {
        targetMemberId,
        address,
        skip: false,
      });

      // If collecting for multiple giftees, move to next
      if (!shippingInfo.collectingForSelf && currentGifteeIndex < shippingInfo.giftees.length - 1) {
        setCurrentGifteeIndex(currentGifteeIndex + 1);
        setAddress({
          name: '',
          phone: '',
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'USA',
        });
        toast.success('Address saved - moving to next giftee');
        setIsSaving(false);
        return;
      }

      toast.success('Shipping address saved!');
      onNavigate('dashboard', { tab: 'todo' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save address';
      toast.error('Failed to save address', {
        description: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border border-primary/20 shadow-lg p-8">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-muted-foreground">Loading shipping information...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !shippingInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border border-destructive/50 shadow-lg p-8">
          <div className="flex flex-col items-center gap-4">
            <AlertCircle className="w-12 h-12 text-destructive" />
            <h2 className="text-xl font-semibold">Error Loading Information</h2>
            <p className="text-muted-foreground text-center">
              {error || 'Failed to load shipping information'}
            </p>
            <Button onClick={() => onNavigate('dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const currentGiftee = !shippingInfo.collectingForSelf && shippingInfo.giftees[currentGifteeIndex];
  const hasAnyAddressData = address.street || address.city || address.state || address.postalCode;

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20">
      {/* Header */}
      <div className="bg-background border-b border-primary/20">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-primary" />
            <div className="flex-1 min-w-0">
              <h1 className="text-primary mb-1">Shipping Address</h1>
              <p className="text-muted-foreground text-sm truncate">
                {shippingInfo.giftGroupTitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="border border-primary/20 shadow-lg">
          <div className="p-6 sm:p-8">
            {/* Info Alert */}
            <Alert className="mb-6 border-primary/30 bg-primary/5">
              <AlertDescription className="flex items-start gap-3">
                {shippingInfo.collectingForSelf ? (
                  <>
                    <User className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-primary mb-1">
                        Enter Your Shipping Address
                      </p>
                      <p className="text-sm text-muted-foreground">
                        We need your shipping address so gifts can be delivered to you. 
                        You can enter what you know now and update it later if needed.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Home className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-primary mb-1">
                        Enter Giftee Shipping Address
                      </p>
                      <p className="text-sm text-muted-foreground">
                        We need the giftee's shipping address for delivery. Enter what you know - 
                        you can skip if you don't have the information yet.
                      </p>
                    </div>
                  </>
                )}
              </AlertDescription>
            </Alert>

            {/* Multiple giftees progress */}
            {!shippingInfo.collectingForSelf && shippingInfo.giftees.length > 1 && (
              <div className="mb-6 p-4 bg-secondary/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">
                  Collecting addresses for {shippingInfo.giftees.length} giftees
                </p>
                <div className="flex items-center gap-2">
                  {shippingInfo.giftees.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 flex-1 rounded-full ${
                        index < currentGifteeIndex
                          ? 'bg-green-500'
                          : index === currentGifteeIndex
                          ? 'bg-primary'
                          : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm font-medium mt-2">
                  {currentGifteeIndex + 1} of {shippingInfo.giftees.length}: {currentGiftee && currentGiftee.name}
                </p>
              </div>
            )}

            {/* Current recipient indicator */}
            {currentGiftee && (
              <div className="mb-6">
                <Label className="text-base">
                  Shipping Address for:{' '}
                  <span className="font-semibold text-primary">{currentGiftee.name}</span>
                </Label>
              </div>
            )}

            {/* Address Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Recipient Name *</Label>
                  <Input
                    id="name"
                    value={address.name}
                    onChange={(e) => setAddress({ ...address, name: e.target.value })}
                    placeholder={currentGiftee ? currentGiftee.name : shippingInfo.collectingForSelf ? "Your Name" : "Recipient Name"}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Contact Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={address.phone}
                    onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                    placeholder="+1-555-0100"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="street">Street Address</Label>
                <Input
                  id="street"
                  value={address.street}
                  onChange={(e) => setAddress({ ...address, street: e.target.value })}
                  placeholder="123 Main Street, Apt 4B"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    placeholder="Springfield"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="state">State / Province</Label>
                  <Input
                    id="state"
                    value={address.state}
                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                    placeholder="IL"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postalCode">ZIP / Postal Code</Label>
                  <Input
                    id="postalCode"
                    value={address.postalCode}
                    onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                    placeholder="62701"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={address.country}
                    onChange={(e) => setAddress({ ...address, country: e.target.value })}
                    placeholder="USA"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button
                onClick={handleSkip}
                variant="outline"
                disabled={isSaving}
                className="flex-1 border-primary/30"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Skip for Now"
                )}
              </Button>

              <Button
                onClick={handleSave}
                disabled={isSaving || !hasAnyAddressData}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Save & Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            {/* Helper Text */}
            <p className="text-xs text-muted-foreground text-center mt-4">
              Don't worry - you can always update this address later from the gift group members page
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
